import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Palmtree, Clock, AlertTriangle, ChevronRight, ClipboardList, Moon } from "lucide-react";

const fmtH = (v) => `${Number(v || 0) > 0 ? "+" : ""}${Number(v || 0).toFixed(1)} h`;

/** Mini resumen RRHH para la home del empleado interno. Lee /portal-empleado/resumen; se oculta si falla. */
export function MiniResumenRRHH() {
  const [r, setR] = useState(undefined);
  const nav = useNavigate();
  useEffect(() => {
    api.get("/portal-empleado/resumen").then((res) => setR(res.data)).catch(() => setR(null));
  }, []);
  if (r === undefined) return <div className="h-20 rounded-xl border border-zinc-800/60 bg-zinc-900/30 animate-pulse mb-4" data-testid="mini-rrhh-loading" />;
  if (!r || !r.mes || r.capacidades?.tipo !== "interno") return null;

  const est = r.estado;
  const abierta = !!est?.abierto;
  const mes = r.mes;
  const provisional = !mes.banco || mes.banco.estado !== "cerrado";
  const pend = r.saldo?.pendiente_total;
  const pendientes = r.solicitudes_pendientes || 0;
  const anomalia = (r.avisos || []).find((a) => ["entrada_huerfana", "discrepancia"].includes(a.tipo));

  return (
    <button onClick={() => nav("/rrhh")} data-testid="mini-rrhh"
      className="w-full text-left bg-zinc-900/50 hover:bg-zinc-900 border border-zinc-800/80 hover:border-indigo-500/40 rounded-xl p-3 mb-4 transition-colors">
      <div className="flex items-center justify-between mb-2">
        <div className="text-[10px] uppercase tracking-wider text-zinc-500 inline-flex items-center gap-1"><Palmtree className="w-3 h-3 text-indigo-300" /> Mi RRHH · resumen</div>
        <span className="text-[11px] text-indigo-300 inline-flex items-center gap-0.5" data-testid="mini-rrhh-ver">Ver mi RRHH <ChevronRight className="w-3 h-3" /></span>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
        <div className={`rounded-lg px-2.5 py-2 border ${abierta ? "bg-emerald-500/5 border-emerald-500/30" : "bg-zinc-950/40 border-zinc-800/60"}`} data-testid="mini-rrhh-jornada">
          <div className="text-[9px] uppercase tracking-wider text-zinc-500 inline-flex items-center gap-1"><Clock className="w-3 h-3" /> Jornada</div>
          <div className={`mt-0.5 font-medium ${abierta ? "text-emerald-300" : "text-zinc-300"}`}>
            {abierta ? (<span className="inline-flex items-center gap-1">{est.jornada_desde_ayer && <Moon className="w-3 h-3 text-indigo-300" />}{est.jornada_abierta_label}</span>) : "Sin jornada abierta"}
          </div>
        </div>
        <div className="rounded-lg px-2.5 py-2 border bg-zinc-950/40 border-zinc-800/60" data-testid="mini-rrhh-mes">
          <div className="text-[9px] uppercase tracking-wider text-zinc-500">Horas del mes</div>
          <div className="mt-0.5 font-mono text-zinc-200">Trabajadas <b>{Number(mes.trabajadas).toFixed(1)} h</b> <span className="text-zinc-500">/ Debía {Number(mes.esperadas).toFixed(1)} h</span></div>
        </div>
        <div className="rounded-lg px-2.5 py-2 border bg-zinc-950/40 border-zinc-800/60" data-testid="mini-rrhh-saldo">
          <div className="text-[9px] uppercase tracking-wider text-zinc-500">Saldo</div>
          <div className="mt-0.5 font-mono">
            <span className={mes.saldo > 0 ? "text-emerald-300" : mes.saldo < 0 ? "text-red-300" : "text-zinc-300"}>{fmtH(mes.saldo)}</span>
            <span className="text-[9px] text-zinc-500 ml-1">{provisional ? "mes provisional" : "mes cerrado"}</span>
            {pend !== undefined && <div className="text-[10px] text-zinc-400">Pendiente acumulado <span className={pend > 0 ? "text-emerald-300" : pend < 0 ? "text-red-300" : "text-zinc-300"}>{fmtH(pend)}</span></div>}
          </div>
        </div>
        <div className={`rounded-lg px-2.5 py-2 border ${pendientes > 0 ? "bg-amber-500/5 border-amber-500/30" : "bg-zinc-950/40 border-zinc-800/60"}`} data-testid="mini-rrhh-solicitudes">
          <div className="text-[9px] uppercase tracking-wider text-zinc-500 inline-flex items-center gap-1"><ClipboardList className="w-3 h-3" /> Solicitudes</div>
          <div className={`mt-0.5 font-medium ${pendientes > 0 ? "text-amber-300" : "text-zinc-300"}`}>{pendientes} pendiente{pendientes === 1 ? "" : "s"}</div>
        </div>
      </div>
      {anomalia && (
        <div className="mt-2 text-[11px] text-amber-200 inline-flex items-start gap-1" data-testid="mini-rrhh-anomalia">
          <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" /> <span>{anomalia.texto}</span>
        </div>
      )}
    </button>
  );
}
